import React, { useEffect, useRef, useState } from 'react';
import { Input, Tag, Spin } from 'antd';
import { GlobalOutlined, TagsOutlined, SearchOutlined, CheckOutlined } from '@ant-design/icons';
import { GET } from '../../utils/apiRequest';
import API from '../../config/apis';
import type { CategoryOption } from './CategorySelect';

export interface DualCategoryValue {
    commonIds?: string[];
    specificId?: string | null;
}

interface Props {
    festId?: string | null;
    value?: DualCategoryValue;
    onChange?: (value: DualCategoryValue) => void;
    disabled?: boolean;
    /** Called once categories are loaded */
    onCategoriesLoaded?: (categories: CategoryOption[]) => void;
}

// Module-level cache shared with CategorySelect
const cache = new Map<string, CategoryOption[]>();

const DualCategorySelect: React.FC<Props> = ({
    festId,
    value = {},
    onChange,
    disabled = false,
    onCategoriesLoaded,
}) => {
    const [categories, setCategories] = useState<CategoryOption[]>([]);
    const [loading, setLoading] = useState(false);
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState('');
    const containerRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<any>(null);

    useEffect(() => {
        if (!festId) { setCategories([]); return; }
        if (cache.has(festId)) {
            const cached = cache.get(festId)!;
            setCategories(cached);
            onCategoriesLoaded?.(cached);
            return;
        }
        setLoading(true);
        GET(`${API.CATEGORIES_AVAILABLE}?festId=${festId}`)
            .then(res => {
                if (res.status) {
                    const raw = res.data?.data || res.data;
                    const list: CategoryOption[] = raw?.rows || (Array.isArray(raw) ? raw : []);
                    cache.set(festId, list);
                    setCategories(list);
                    onCategoriesLoaded?.(list);
                }
            })
            .catch(() => {})
            .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [festId]);

    // Close dropdown on outside click
    useEffect(() => {
        const handler = (e: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
                setOpen(false);
                setSearch('');
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    const common = categories.filter(c => c.scope === 'common');
    const specific = categories.filter(c => c.scope !== 'common');

    const q = search.toLowerCase();
    const filteredCommon = q ? common.filter(c => c.name.toLowerCase().includes(q)) : common;
    const filteredSpecific = q ? specific.filter(c => c.name.toLowerCase().includes(q)) : specific;

    const selectedCommons = categories.filter(c => (value.commonIds || []).includes(c.id));
    const selectedSpecific = categories.find(c => c.id === value.specificId);

    const handleSelect = (cat: CategoryOption) => {
        if (cat.scope === 'common') {
            const current = value.commonIds || [];
            const updated = current.includes(cat.id)
                ? current.filter(id => id !== cat.id)
                : [...current, cat.id];
            onChange?.({ ...value, commonIds: updated });
        } else {
            onChange?.({ ...value, specificId: value.specificId === cat.id ? null : cat.id });
        }
    };

    const renderRow = (cat: CategoryOption) => {
        const isSelected = cat.scope === 'common'
            ? (value.commonIds || []).includes(cat.id)
            : value.specificId === cat.id;
        return (
            <div
                key={cat.id}
                className={[
                    'flex items-center gap-2 px-3 py-2 cursor-pointer rounded-md transition-colors text-sm select-none',
                    isSelected
                        ? 'bg-primary/15 text-primary font-medium'
                        : 'hover:bg-muted/60 text-foreground',
                ].join(' ')}
                onMouseDown={e => { e.preventDefault(); handleSelect(cat); }}
            >
                {cat.scope === 'common'
                    ? <GlobalOutlined className="text-blue-400 shrink-0 text-xs" />
                    : <TagsOutlined className="text-muted-foreground shrink-0 text-xs" />}
                <span className="flex-1 truncate">{cat.name}</span>
                <Tag
                    className="!ml-auto !text-[10px] !px-1.5 !py-0 !leading-4 !rounded-full !border-0 shrink-0"
                    color={cat.scope === 'common' ? 'blue' : 'default'}
                >
                    {cat.scope === 'common' ? 'Common' : 'Category'}
                </Tag>
                {isSelected && <CheckOutlined className="text-primary shrink-0 text-xs" />}
            </div>
        );
    };

    const hasSelections = selectedCommons.length > 0 || selectedSpecific;
    const noFest = !festId;

    return (
        <div ref={containerRef} className="relative">
            {/* Selected chips */}
            {hasSelections && (
                <div className="flex flex-wrap gap-1.5 mb-2">
                    {selectedCommons.map(sc => (
                        <Tag
                            key={sc.id}
                            color="blue"
                            closable={!disabled}
                            onClose={e => { e.preventDefault(); onChange?.({ ...value, commonIds: (value.commonIds || []).filter(id => id !== sc.id) }); }}
                            icon={<GlobalOutlined />}
                            className="!rounded-full !text-xs !py-0.5"
                        >
                            {sc.name}
                        </Tag>
                    ))}
                    {selectedSpecific && (
                        <Tag
                            closable={!disabled}
                            onClose={e => { e.preventDefault(); onChange?.({ ...value, specificId: null }); }}
                            icon={<TagsOutlined />}
                            className="!rounded-full !text-xs !py-0.5"
                        >
                            {selectedSpecific.name}
                        </Tag>
                    )}
                </div>
            )}

            {/* Search input (trigger) */}
            <Input
                ref={inputRef}
                prefix={<SearchOutlined className="text-muted-foreground" />}
                placeholder={noFest ? 'Select festival first…' : 'Search categories…'}
                value={search}
                onChange={e => setSearch(e.target.value)}
                onFocus={() => { if (!disabled && !noFest) setOpen(true); }}
                disabled={disabled || noFest}
                suffix={loading ? <Spin size="small" /> : null}
                size="large"
                autoComplete="off"
            />

            {/* Dropdown panel */}
            {open && !disabled && !noFest && (
                <div
                    className="absolute z-50 left-0 right-0 mt-1 rounded-lg border border-border shadow-xl overflow-hidden"
                    style={{ background: 'var(--color-popover, hsl(var(--popover)))' }}
                >
                    <div className="max-h-64 overflow-y-auto p-1.5">
                        {loading && (
                            <div className="py-6 text-center text-muted-foreground text-xs">
                                <Spin size="small" />
                            </div>
                        )}

                        {!loading && filteredCommon.length === 0 && filteredSpecific.length === 0 && (
                            <div className="py-6 text-center text-xs text-muted-foreground space-y-1">
                                {search
                                    ? <span>No categories match "<strong>{search}</strong>"</span>
                                    : <span>No categories available. Create one first.</span>}
                            </div>
                        )}

                        {filteredCommon.length > 0 && (
                            <div className="mb-1">
                                <div className="px-2 pt-1 pb-0.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                                    Common Categories
                                </div>
                                {filteredCommon.map(renderRow)}
                            </div>
                        )}

                        {filteredSpecific.length > 0 && (
                            <div>
                                <div className="px-2 pt-2 pb-0.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                                    Categories
                                </div>
                                {filteredSpecific.map(renderRow)}
                            </div>
                        )}
                    </div>

                    <div className="border-t border-border px-3 py-1.5 text-[10px] text-muted-foreground bg-muted/20 flex items-center gap-1">
                        <span>Common: <strong>multiple</strong> allowed — Category: <strong>one</strong> only</span>
                    </div>
                </div>
            )}
        </div>
    );
};

export { cache as dualCategoryCache };
export default DualCategorySelect;
